// Content for SEO / audience landing pages. Each entry is rendered by
// components/SeoHeadshotPage/index.js. Keep copy honest: results are AI-generated.

const HOW_IT_WORKS = [
  { title: 'Upload your photos', desc: 'Add a few clear, well-lit selfies of yourself.' },
  { title: 'Choose your styles', desc: 'Pick professional outfits and backgrounds.' },
  { title: 'Our AI generates', desc: 'Our AI creates a batch of professional headshots.' },
  { title: 'Download & use', desc: 'Download your favorites and use them anywhere.' },
];

export const SEO_PAGES = {
  linkedin: {
    h1: 'AI Headshots for LinkedIn',
    intro:
      'Stand out on LinkedIn with polished, professional AI-generated headshots. Upload a few selfies, choose your style, and get profile-ready images in about an hour — no studio booking required.',
    howItWorks: HOW_IT_WORKS,
    sections: [
      {
        heading: 'Why a great LinkedIn photo matters',
        list: [
          'Profiles with a clear, professional photo get noticed more by recruiters and connections.',
          'A consistent, on-brand headshot makes you look credible and approachable.',
          'You get many looks and backgrounds to match your industry.',
        ],
      },
      {
        heading: 'Tips for the best LinkedIn headshot',
        list: [
          'Use a neutral expression or a natural smile.',
          'Upload photos with good lighting and a clear view of your face.',
          'Pick a background that fits your field (office, neutral, or outdoor).',
        ],
      },
    ],
  },

  realtors: {
    h1: 'AI Headshots for Realtors',
    intro:
      'Real estate is a people business — your headshot is your handshake. Get approachable, trustworthy AI-generated headshots for your listings, business cards, yard signs, and social profiles.',
    howItWorks: HOW_IT_WORKS,
    sections: [
      {
        heading: 'Built for real estate professionals',
        list: [
          'Warm, friendly, and professional looks that build trust with clients.',
          'Multiple outfits and backgrounds for listings, signage, and social media.',
          'Refresh your brand photo in minutes whenever you need to.',
        ],
      },
    ],
  },

  executives: {
    h1: 'AI Headshots for Executives',
    intro:
      'Project authority and approachability with executive-grade AI-generated headshots for company bios, speaker pages, press, and board profiles.',
    howItWorks: HOW_IT_WORKS,
    sections: [
      {
        heading: 'Executive presence, on demand',
        list: [
          'Refined, professional styles suited to leadership profiles.',
          'Consistent imagery for company websites, annual reports, and press.',
          'Quickly produce updated photos for new roles or announcements.',
        ],
      },
    ],
  },

  teams: {
    h1: 'AI Headshots for Teams',
    intro:
      'Give your whole team a consistent, professional look. Each person uploads their own photos and receives AI-generated headshots in a matching style for your About and Team pages.',
    howItWorks: HOW_IT_WORKS,
    sections: [
      {
        heading: 'Consistent branding across your company',
        list: [
          'A unified style and background for every team member.',
          'No scheduling a group photo day — everyone uploads on their own time.',
          'Easy to add new hires with matching headshots later.',
        ],
      },
      {
        heading: 'Working with larger teams',
        body:
          'Outfitting a bigger team? Contact us and we can help with bulk setup and a consistent style across everyone.',
      },
    ],
  },

  'job-seekers': {
    h1: 'AI Headshots for Job Seekers',
    intro:
      'Make a strong first impression on applications and interviews. Get professional AI-generated headshots for your resume, LinkedIn, and portfolio — affordably and fast.',
    howItWorks: HOW_IT_WORKS,
    sections: [
      {
        heading: 'Put your best face forward',
        list: [
          'Professional looks for resumes, LinkedIn, and personal sites.',
          'Affordable alternative to a studio session while you job hunt.',
          'Several styles so you can match the role and industry.',
        ],
      },
    ],
  },

  'upload-guide': {
    h1: 'AI Headshot Upload Guide',
    intro:
      'Better input photos produce better AI headshots. Follow these guidelines to get the most out of your session.',
    sections: [
      {
        heading: 'Do upload',
        list: [
          'Clear, well-lit, in-focus photos.',
          'A variety of angles, expressions, and backgrounds.',
          'Recent photos that look like you today.',
          'Photos where your full face is visible.',
        ],
      },
      {
        heading: 'Please avoid',
        list: [
          'Sunglasses, hats, or anything covering your face.',
          'Heavy filters or beauty effects.',
          'Group photos — upload only photos of yourself.',
          'Blurry, dark, or low-resolution images.',
          'Extreme angles or faces that are very small in the frame.',
        ],
      },
      {
        heading: 'Why it matters',
        body:
          'The AI learns what you look like from the photos you provide. Clear, varied photos give it the best information, which leads to more accurate and natural-looking headshots. Remember that results are AI-generated and may vary with the quality of your uploads.',
      },
    ],
  },

  'privacy-data-deletion': {
    h1: 'AI Headshot Privacy & Data Deletion',
    intro:
      'Your privacy comes first. Here is a plain-language summary of how we handle your photos and how to delete your data. For full details, see our Privacy Policy.',
    sections: [
      {
        heading: 'What we promise',
        list: [
          'We do not sell your uploaded photos.',
          'Your photos are used only to create your headshots.',
          'You own your final generated headshots.',
          'We train a private, single-purpose AI model for you only — we do not use your photos to train shared or foundation models.',
        ],
      },
      {
        heading: 'How long we keep your data',
        list: [
          'Uploaded photos are automatically deleted within 30 days.',
          'Generated headshots are automatically deleted within 30 days — download what you want to keep.',
          'You can request deletion at any time and we will process it promptly.',
        ],
      },
      {
        heading: 'How to delete your data',
        body:
          'Email support@headshotsnow.ai from the address on your account to request deletion. See our Privacy Policy for the complete details.',
      },
    ],
  },
};
